<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="showSearch"
        v-closeOnPressEsc="close"
        class="fixed top-0 left-0 p-15 w-full h-full bg-backdrop flex justify-center items-start z-100"
        @click.self="close"
      >
        <div
          class="relative py-20 bg-book rounded-6 w-full max-w-500 top-40 md:top-1/5"
          :class="isDarkMode ? 'shadow-search-dark' : 'shadow-dropdown'"
        >
          <div>
            <div class="relative">
              <label class="flex items-center border-3 border-app-color rounded-6 p-10 mx-20">
                <i class="icon-search mr-2 ml-5 font-bold text-app-color cursor-pointer" />
                <input
                  ref="inputRef"
                  v-model="searchValue"
                  type="text"
                  placeholder="Search"
                  class="w-full text-app-color bg-transparent pr-10 pl-5 focus:outline-none placeholder-app-color opacity-50 focus:opacity-100 anim-opacity"
                  @keydown="arrowNavigation"
                  @input="search"
                >
                <span class="flex-shrink-0 text-14 mr-5 font-semibold text-app-color opacity-30">ctrl + k</span>
              </label>
              <div v-if="filtered.length > 0" class="w-full max-h-355 overflow-y-auto mt-15 pt-1">
                <div v-if="!searchValue" class="px-20 pt-10 pb-2">
                  <span class="text-17 font-bold text-app-color">Recent</span>
                </div>

                <!-- v-show="item.hasHighlight" -->
                <div
                  v-for="(item, index) in filtered"
                  :key="item.id + item.title"
                  :ref="el => indexRefs[index] = el"
                  class="search flex justify-between items-center px-20 py-9 mx-20 bg-transparent cursor-pointer group mb-10 rounded-6 group text-app-color"
                  :class="[
                    activeItemClass(index),
                    searchValue ? `search-${item._index}` : ''
                  ]"
                  @mousedown.prevent.stop
                  @click="select(item)"
                >
                  <component
                    :is="item._index"
                    :ref="el => componentRef = el"
                    :item="item"
                    :active-index="index === activeIndex"
                    :query="query"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, computed, ref, nextTick, onMounted, onBeforeUpdate, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import localStorageService from '@/core/local-storage'
import { searchService } from '@/services'

import BookLabel from '@/components/books/BookLabel'
import project from '@/components/search/project'
import book from '@/components/search/book'
import event from '@/components/search/event'
import task from '@/components/search/task'
import discussion from '@/components/search/discussion'
import message from '@/components/search/message'
import discussionMessage from '@/components/search/discussionMessage'

export default defineComponent({
  name: 'SearchModal',

  components: {
    BookLabel,
    project,
    book,
    event,
    task,
    discussion,
    message,
    discussionMessage
  },

  setup () {
    const { state, getters, commit } = useStore()
    const router = useRouter()

    const indexRefs = ref([])
    const inputRef = ref(null)
    const componentRef = ref(null)

    const showSearch = computed(() => state.general.showSearch)
    const searchValue = ref('')
    const activeIndex = ref(0)

    const filtered = ref([])
    const query = ref('')

    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isMobile = computed(() => getters.isMobile)

    const books = computed(() => state.book.books)
    const projects = computed(() => state.project.projects)
    const user = computed(() => state.profile.user)
    const team = computed(() => state.team.team)
    const headquarters = computed(() => state.team.headquarters)

    const activeItemClass = computed(() => index => {
      const prefix = index === activeIndex.value ? '' : 'hover:'
      const shadow = index === activeIndex.value ? 'shadow-light' : 'shadow-event'

      return isDarkMode.value ? `${prefix}bg-selection shadow-event-dark` : `${prefix}bg-grey-1100 ${shadow}`
    })

    const title = computed(() => {
      return !searchValue.value && filtered.value.length ? 'Recent' : 'Pages and content'
    })

    const items = computed(() => {
      const bks = books.value.filter(item => !item.isSection).map(item => ({ ...item, label: 'book' }))
      return [...bks, ...projects.value]
    })

    const recentOpened = computed(() => {
      const id = user.value.id
      const allRecentFiles = localStorageService.getItem('search-recent') || {}
      return allRecentFiles[id] || []
    })

    watch(showSearch, value => {
      if (value) filtered.value = recentOpened.value
    })

    onMounted(async () => {
      searchService.search({ query: '' })
      localStorageService.removeItem('recent-opened-items')
    })

    // to reset the refs before each update
    onBeforeUpdate(() => {
      indexRefs.value = []
    })

    watch(showSearch, (value) => {
      if (value) nextTick(() => inputRef.value.focus())
    })

    function close () {
      activeIndex.value = 0
      searchValue.value = ''
      query.value = ''
      filtered.value = []
      commit('toggleSearch', false)
    }

    function arrowNavigation (e) {
      if (filtered.value.length) {
        if (e.keyCode === 38 && activeIndex.value > 0) {
          e.preventDefault()
          activeIndex.value--
          indexRefs.value[activeIndex.value].scrollIntoView(false)
        }
        if (e.keyCode === 40 && activeIndex.value < filtered.value.length - 1) {
          e.preventDefault()
          activeIndex.value++
          indexRefs.value[activeIndex.value].scrollIntoView(false)
        }
        if (e.keyCode === 13) {
          e.preventDefault()
          select(filtered.value[activeIndex.value])
        }
      }
    }

    function redirect (item) {
      commit('setHighlightedText', query.value)

      if (item._index === 'project') {
        if (isTeamAccount.value) {
          const name = item.bookId === headquarters.value?.id ? 'Headquarters' : 'TeamBook'

          router.push({ name: name, params: { team: team.value.link, id: item.bookId }, query: { page: item.id, highlight: true } })
        } else {
          router.push({ name: 'Book', params: { id: item.bookId }, query: { page: item.id, highlight: true } })
        }
      } else if (item._index === 'book') {
        if (isTeamAccount.value) {
          const name = item.id === headquarters.value?.id ? 'Headquarters' : 'TeamBook'

          router.push({ name: name, params: { team: team.value.link, id: item.id } })
        } else {
          router.push({ name: 'Book', params: { id: item.id } })
        }
      } else if (item._index === 'event' || item._index === 'task' || item._index === 'discussion') {
        const teamNames = { event: 'WeekPlannerTeam', task: 'TaskBoardTeam', discussion: 'Discussions' }
        const personalNames = { event: 'WeekPlanner', task: 'TaskBoard' }

        const name = isTeamAccount.value ? teamNames[item._index] : personalNames[item._index]
        const params = isTeamAccount.value ? { team: team.value.link, id: item.bookId } : { id: item.bookId }

        const q = { spaces: item.bookId, highlight: item.id }

        if (item._index === 'discussion' && item.highlight?.content) {
          return router.push({ name: 'Discussion', params: { team: team.value.link, id: item.bookId, discussionId: item.id }, query: { content: true } })
        }

        if (item.highlight?.additionalInfo) {
          q.additionalInfo = true
        }

        if (item.resolvedAt) {
          q.resolved = true
        }

        router.push({ name: name, params: params, query: q })
      } else if (item._index === 'message') {
        const name = item.chat.bookId === headquarters.value?.id ? 'Headquarters' : 'TeamBook'

        router.push({ name: name, params: { team: team.value.link, id: item.chat.bookId }, query: { openChat: true, message: item.id } })
      }
    }

    function select (item) {
      redirect(item)

      recentFiles(item)

      close()

      if (isMobile.value) commit('updateSidebar', false)
    }

    function recentFiles (item) {
      const allRecentOpened = localStorageService.getItem('search-recent') || {}
      const userRecentOpened = allRecentOpened[user.value.id] || []

      if (userRecentOpened.length > 3) {
        userRecentOpened.shift()
      }

      const isItemInRecentOpened = userRecentOpened.some(recentOpenedItem => recentOpenedItem.id === item.id)
      !isItemInRecentOpened && userRecentOpened.unshift(item)

      allRecentOpened[user.value.id] = userRecentOpened
      localStorageService.setItem('search-recent', allRecentOpened)
    }

    function checkHighlight (text) {
      var span = document.createElement('span')
      span.innerHTML = text?.replace(/(<([^>]+)>)/ig, '\n')

      const splittedText = span.textContent.toLowerCase().split('\n')
      const highlight = splittedText.find(item => item.includes(searchValue.value.toLowerCase()))
      if (highlight === 'use template') return false

      return true
    }

    let timer = null

    function search () {
      clearTimeout(timer)

      timer = setTimeout(() => {
        query.value = searchValue.value
        searchService.search({ query: searchValue.value })
          .then(res => {
            const filteredData = res.data.result?.filter(item => item._index !== 'discussion_message') || []

            const arr = filteredData.map(item => {
              let hasHighlight = item.highlight

              if (item._index === 'project') {
                if (item._source.body && item.highlight?.body) {
                  hasHighlight = checkHighlight(item._source.body)
                }
              }

              return {
                ...item,
                ...item._source,
                _index: item._index,
                title: item._source.title || item._source.text,
                highlight: item.highlight,
                hasHighlight: hasHighlight
              }
            })

            filtered.value = arr.filter(item => item.hasHighlight) || []
          })
      }, 300)
    }

    return {
      // refs
      inputRef,
      indexRefs,
      componentRef,
      // data
      showSearch,
      searchValue,
      activeIndex,
      filtered,
      query,
      // computed
      isTeamAccount,
      isDarkMode,
      books,
      projects,
      user,
      team,
      headquarters,
      items,
      title,
      recentOpened,
      activeItemClass,
      // function
      close,
      search,
      arrowNavigation,
      select,
      recentFiles
    }
  }
})
</script>
